import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { EFFECT_GENERATORS } from '../utils/profileEffects';
import { useLang } from './LangContext';
import '../styles/Profile.css';

/* ── display maps ── */
const ROLE_MAP = {
  ban_dieu_hanh: 'Ban Điều Hành',
  ban_dao_tao: 'Ban Đào Tạo',
  ban_su_kien: 'Ban Sự Kiện',
  ky_thuat: 'Kỹ Thuật',
  truyen_thong: 'Truyền Thông',
  nhan_su: 'Nhân Sự',
  su_kien: 'Sự Kiện',
  thanh_vien: 'Thành Viên',
};
const BAN_PRIORITY = ['ban_dieu_hanh', 'ban_dao_tao', 'ban_su_kien', 'ky_thuat'];
// Bans we actually display in the public role/tag area. ky_thuat is a
// behind-the-scenes tech role and shouldn't be shown alongside the visible bans.
const VISIBLE_BANS = ['ban_dieu_hanh', 'ban_dao_tao', 'ban_su_kien'];
const pickPrimaryBan = (roles) => {
  const arr = Array.isArray(roles) ? roles : (roles ? [roles] : []);
  return BAN_PRIORITY.find(k => arr.includes(k)) || null;
};
const pickAllBans = (roles) => {
  const arr = Array.isArray(roles) ? roles : (roles ? [roles] : []);
  return VISIBLE_BANS.filter(k => arr.includes(k));
};

const POSITION_MAP = {
  mam_non: 'Mầm Non',
  tap_su: 'Tập Sự',
  culi_cap_cao: 'Culi Cấp Cao',
  bo_lao: 'Bô Lão',
  founder: 'Founder',
};

const TITLE_MAP = {
  founder: 'Founder',
  chu_nhiem: 'Chủ Nhiệm',
  pho_chu_nhiem: 'Phó Chủ Nhiệm',
  truong_ban_dao_tao: 'Trưởng Ban Đào Tạo',
  pho_ban_dao_tao: 'Phó Ban Đào Tạo',
  truong_ban_su_kien: 'Trưởng Ban Sự Kiện',
  pho_ban_su_kien: 'Phó Ban Sự Kiện',
};

const CTF_FIELD_MAP = {
  reverse_engineering: 'Reverse Engineering',
  pwnable: 'Binary Exploitation',
  web_exploitation: 'Web Exploitation',
  cryptography: 'Cryptography',
  forensics: 'Digital Forensics',
  osint: 'Open Source Intelligence',
};

const CTF_ICON = {
  reverse_engineering: '⚙',
  pwnable: '⚡',
  web_exploitation: '🌐',
  cryptography: '🔐',
  forensics: '🔍',
  osint: '🔎',
};

/* ── role display rule ── */
const getDisplayRole = (roles) => {
  if (!roles || roles.length === 0) return null;
  if (roles.includes('ban_dieu_hanh')) return 'Ban Điều Hành';
  if (roles.includes('thanh_vien')) return 'Thành Viên';
  const first = roles.find((r) => r !== 'ky_thuat') || roles[0];
  return ROLE_MAP[first] || first;
};

/* ── effect generators ── */
// All effect generators (snowflakes, particles, stars, soundwave, streaks,
// bubbles, matrix, fireflies) live in `utils/profileEffects.js` so the
// dashboard customization preview renders identically to the public profile
// page. EFFECT_GENERATORS is imported at the top of this file.

/* ── initials avatar ── */
const InitialsAvatar = ({ name }) => {
  const initials = (name || '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
  return (
    <div className="pf-avatar-initials">
      <span>{initials}</span>
    </div>
  );
};

/* ── photo carousel ── */
const PhotoCarousel = ({ photos }) => {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!photos || photos.length <= 1) return;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % photos.length);
    }, 4000);
    return () => clearInterval(timerRef.current);
  }, [photos]);

  if (!photos || photos.length === 0) return null;

  return (
    <div className="pf-photo-carousel">
      {photos.map((p, i) => (
        <img
          key={i}
          src={`/uploads/profiles/${p}`}
          alt=""
          className={`pf-photo-slide ${i === idx ? 'is-active' : ''}`}
        />
      ))}
      {photos.length > 1 && (
        <div className="pf-photo-dots">
          {photos.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`pf-photo-dot ${i === idx ? 'is-active' : ''}`}
              onClick={() => setIdx(i)}
              aria-label={`Photo ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── auto-fit name on one line ── */
const FitName = ({ name }) => {
  const wrapRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    const fit = () => {
      const wrap = wrapRef.current;
      const el = textRef.current;
      if (!wrap || !el) return;
      const max = 56; // px
      const min = 18; // px
      el.style.fontSize = `${max}px`;
      let size = max;
      while (el.scrollWidth > wrap.clientWidth && size > min) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [name]);

  return (
    <div ref={wrapRef} className="pf-hero-name-wrap">
      <h1 ref={textRef} className="pf-hero-name">{name}</h1>
    </div>
  );
};

/* ── macOS-style music player ── */
const formatTime = (s) => {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${ss}`;
};

const MusicPlayer = ({ playlist, settings, ownerName }) => {
  const lang = useLang();
  const tracks = Array.isArray(playlist) ? playlist : [];
  const autoPlay = !!(settings && settings.autoPlay);
  const initialLoop = (settings && settings.loopMode) || 'none';

  const [open, setOpen] = useState(false);
  const [trackIdx, setTrackIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [loopMode, setLoopMode] = useState(initialLoop);
  const audioRef = useRef(null);
  const autoPlayTried = useRef(false);

  const currentTrack = tracks[trackIdx] || null;

  // Apply volume
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // When src changes, reset time and try autoplay if enabled
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.load();
    if (autoPlay || playing) {
      audioRef.current.play().catch(() => {/* blocked until gesture */});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackIdx]);

  // First-mount autoplay attempt
  useEffect(() => {
    if (autoPlay && !autoPlayTried.current && audioRef.current && currentTrack) {
      autoPlayTried.current = true;
      audioRef.current.play().catch(() => {/* user must click play later */});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack]);

  if (!tracks.length) return null;

  const togglePlay = () => {
    const a = audioRef.current; if (!a) return;
    if (playing) a.pause(); else a.play().catch(() => {});
  };

  const next = () => {
    if (!tracks.length) return;
    setTrackIdx(i => (i + 1) % tracks.length);
  };
  const prev = () => {
    if (!tracks.length) return;
    setTrackIdx(i => (i - 1 + tracks.length) % tracks.length);
  };

  const handleEnded = () => {
    if (loopMode === 'one') {
      const a = audioRef.current; if (!a) return;
      a.currentTime = 0; a.play().catch(() => {});
    } else if (loopMode === 'all') {
      next();
    } else {
      // none: advance if not last, else stop
      if (trackIdx < tracks.length - 1) next();
      else setPlaying(false);
    }
  };

  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const pct = parseFloat(e.target.value) / 100;
    a.currentTime = pct * duration;
    setCurrent(a.currentTime);
  };

  const cycleLoop = () => {
    setLoopMode(m => m === 'none' ? 'all' : (m === 'all' ? 'one' : 'none'));
  };

  return (
    <div className="mac-player-anchor">
      <audio
        ref={audioRef}
        src={currentTrack ? `/uploads/music/${currentTrack.filename}` : ''}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={handleEnded}
        onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
        onTimeUpdate={(e) => setCurrent(e.target.currentTime || 0)}
        preload="metadata"
      />

      <AnimatePresence mode="wait" initial={false}>
        {!open ? (
          <motion.div
            key="bar"
            initial={{ opacity: 0, y: -10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="mac-player mac-player--bar"
            onClick={() => setOpen(true)}
            role="button"
            tabIndex={0}
            aria-label={lang.PROFILE_OPEN_LIST || 'Mở danh sách nhạc'}
            title={lang.PROFILE_OPEN_LIST || 'Mở danh sách nhạc'}
          >
            {/* Compact bubble — the whole pill is the expand trigger so
                users just see a small disc icon in the corner. We preserve
                the inner controls for desktop-wide layouts by hiding them
                via CSS rather than removing them. */}
            <button
              type="button"
              className={`mac-bar-disc ${playing ? 'spinning' : ''}`}
              onClick={(e) => { e.stopPropagation(); setOpen(true); }}
              aria-label={lang.PROFILE_OPEN_LIST || 'Mở danh sách nhạc'}
            >
              ♪
            </button>
            <div className="mac-bar-info" onClick={() => setOpen(true)} role="button" tabIndex={0}>
              <div className="mac-bar-title">{currentTrack?.title || 'Untitled'}</div>
              <div className="mac-bar-sub">{ownerName || ''}</div>
            </div>
            {tracks.length > 1 && (
              <>
                <button type="button" className="mac-bar-btn" onClick={prev} aria-label={lang.PROFILE_PREV || 'Previous'}>⏮</button>
                <button type="button" className="mac-bar-btn" onClick={next} aria-label={lang.PROFILE_NEXT || 'Next'}>⏭</button>
              </>
            )}
            <button
              type="button"
              className="mac-bar-btn mac-bar-expand"
              onClick={() => setOpen(true)}
              aria-label={lang.PROFILE_EXPAND || 'Expand'}
              title={lang.PROFILE_OPEN_LIST || 'Mở danh sách'}
            >▾</button>
          </motion.div>
        ) : (
          <motion.div
            key="card"
            initial={{ opacity: 0, y: -16, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.94 }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            className="mac-player mac-player--card"
            role="region"
            aria-label={lang.PROFILE_MUSIC_PLAYER || 'Music player'}
          >
            {/* traffic lights */}
            <div className="mac-traffic">
              <button
                type="button"
                className="mac-dot close"
                onClick={() => setOpen(false)}
                aria-label={lang.PROFILE_MINIMIZE || 'Minimize'}
                title={lang.PROFILE_COLLAPSE || 'Thu gọn'}
              />
              <span className="mac-dot min" />
              <span className="mac-dot max" />
            </div>

            <div className="mac-card-art">
              <div className={`mac-card-disc ${playing ? 'spinning' : ''}`}>
                <span>♪</span>
              </div>
            </div>

            <div className="mac-card-info">
              <div className="mac-card-title" title={currentTrack?.title}>
                {currentTrack?.title || 'Untitled'}
              </div>
              <div className="mac-card-artist">{ownerName || ''}</div>
              <div className="mac-card-counter">
                Bài {trackIdx + 1} / {tracks.length}
              </div>
            </div>

            <div className="mac-card-progress">
              <span className="mac-time">{formatTime(current)}</span>
              <input
                type="range"
                min="0" max="100" step="0.1"
                value={duration ? (current / duration) * 100 : 0}
                onChange={onSeek}
                aria-label={lang.PROFILE_SEEK || 'Seek'}
              />
              <span className="mac-time">{formatTime(duration)}</span>
            </div>

            <div className="mac-card-controls">
              <button
                type="button"
                className={`mac-card-btn mac-loop ${loopMode}`}
                onClick={cycleLoop}
                title={loopMode === 'none' ? 'Không lặp' : loopMode === 'one' ? 'Lặp 1 bài' : 'Lặp danh sách'}
                aria-label={loopMode === 'none' ? 'Không lặp' : loopMode === 'one' ? 'Lặp 1 bài' : 'Lặp danh sách'}
              >
                <svg className="mac-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="17 1 21 5 17 9" />
                  <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                  <polyline points="7 23 3 19 7 15" />
                  <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                  {loopMode === 'one' && <text x="12" y="15" textAnchor="middle" fontSize="9" fontWeight="700" fill="currentColor" stroke="none">1</text>}
                </svg>
                {loopMode === 'none' && <span className="mac-loop-off">/</span>}
              </button>
              <button type="button" className="mac-card-btn" onClick={prev} aria-label={lang.PROFILE_PREV || 'Previous'}>
                <svg className="mac-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <polygon points="19,4 9,12 19,20" />
                  <rect x="5" y="4" width="2" height="16" />
                </svg>
              </button>
              <button
                type="button"
                className="mac-card-btn mac-card-play"
                onClick={togglePlay}
                aria-label={playing ? 'Pause' : 'Play'}
              >
                {playing ? (
                  <svg className="mac-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg className="mac-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <polygon points="6,4 20,12 6,20" />
                  </svg>
                )}
              </button>
              <button type="button" className="mac-card-btn" onClick={next} aria-label={lang.PROFILE_NEXT || 'Next'}>
                <svg className="mac-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <polygon points="5,4 15,12 5,20" />
                  <rect x="17" y="4" width="2" height="16" />
                </svg>
              </button>
              <div className="mac-card-volume">
                <svg className="mac-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <polygon points="3,9 7,9 12,4 12,20 7,15 3,15" />
                  <path d="M16 8a5 5 0 0 1 0 8" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
                </svg>
                <input
                  type="range"
                  min="0" max="1" step="0.01"
                  value={volume}
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                  aria-label={lang.PROFILE_VOLUME || 'Volume'}
                />
              </div>
            </div>

            {/* Up Next list */}
            <div className="mac-card-list">
              <div className="mac-card-list-head">Danh sách bài hát ({tracks.length})</div>
              <ul>
                {tracks.map((t, i) => (
                  <li
                    key={t.id || i}
                    className={`mac-card-list-item ${i === trackIdx ? 'active' : ''}`}
                    onClick={() => { setTrackIdx(i); setPlaying(true); }}
                    role="button"
                    tabIndex={0}
                  >
                    <span className="mac-card-list-num">{i === trackIdx && playing ? '♪' : (i + 1).toString().padStart(2, '0')}</span>
                    <span className="mac-card-list-title">{t.title || t.originalName || 'Untitled'}</span>
                    {t.size > 0 && <span className="mac-card-list-size">{(t.size / 1024 / 1024).toFixed(1)}MB</span>}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── personal blog section (Jekyll-style) ── */
const PersonalBlog = ({ username }) => {
  const lang = useLang();
  const [posts, setPosts] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch(`/api/profile/${username}/posts`)
      .then(r => r.ok ? r.json() : { posts: [] })
      .then(d => setPosts(d.posts || []))
      .catch(() => setPosts([]));
  }, [username]);

  useEffect(() => {
    document.body.style.overflow = selected ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selected]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!posts.length) {
    return (
      <div className="pf-empty-view">
        <p>{lang.FORUM_NO_POSTS || 'Chưa có bài viết nào.'}</p>
      </div>
    );
  }

  const fmt = (d) => {
    const date = new Date(d);
    if (isNaN(date.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${p(date.getDate())}/${p(date.getMonth() + 1)}/${date.getFullYear()}`;
  };

  return (
    <div className="pf-blog-view">
      <ul className="pf-blog-list">
        {posts.map(p => (
          <li
            key={p.id}
            className="pf-blog-item"
            onClick={() => setSelected(p)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') setSelected(p); }}
          >
            <div className="pf-blog-meta">
              <time>{fmt(p.createdAt)}</time>
              {p.tags && p.tags.length > 0 && (
                <span className="pf-blog-tags">
                  {p.tags.map((t, i) => <span key={i} className="pf-blog-tag">#{t}</span>)}
                </span>
              )}
            </div>
            <h3 className="pf-blog-item-title">{p.title}</h3>
            {p.excerpt && <p className="pf-blog-item-excerpt">{p.excerpt}</p>}
            <span className="pf-blog-read">{lang.PROFILE_READ_MORE || 'Read →'}</span>
          </li>
        ))}
      </ul>

      {selected && (
        <div className="pf-blog-overlay" onClick={() => setSelected(null)}>
          <article className="pf-blog-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pf-blog-close" onClick={() => setSelected(null)} aria-label={lang.COMMON_CLOSE || 'Close'}>✕</button>
            <time className="pf-blog-modal-date">{fmt(selected.createdAt)}</time>
            <h1 className="pf-blog-modal-title">{selected.title}</h1>
            {selected.tags && selected.tags.length > 0 && (
              <div className="pf-blog-tags">
                {selected.tags.map((t, i) => <span key={i} className="pf-blog-tag">#{t}</span>)}
              </div>
            )}
            <div className="pf-blog-modal-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {selected.content || ''}
              </ReactMarkdown>
            </div>
          </article>
        </div>
      )}
    </div>
  );
};

/* ── personal achievements (Jekyll/timeline) ── */
const PersonalAchievements = ({ items }) => {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) {
    return (
      <div className="pf-empty-view">
        <p>Chưa có thành tựu cá nhân nào.</p>
      </div>
    );
  }

  // Sort: most recent (by date string) first; items without date go last
  const sorted = [...list].sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    return db - da;
  });

  const yearOf = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? d : dt.getFullYear().toString();
  };

  return (
    <div className="pf-pa-timeline">
      <div className="pf-pa-line" />
      {sorted.map((item, idx) => {
        const side = idx % 2 === 0 ? 'left' : 'right';
        const hasImages = Array.isArray(item.images) && item.images.length > 0;
        return (
          <div key={item.id} className={`pf-pa-item ${side}`}>
            <div className="pf-pa-dot"><div className="pf-pa-dot-inner" /></div>
            <div className="pf-pa-year">{yearOf(item.date)}</div>
            <div className={`pf-pa-card ${hasImages ? 'has-images' : ''}`}>
              <div className="pf-pa-text">
                <h3 className="pf-pa-title">{item.title}</h3>
                {item.description && <p className="pf-pa-desc">{item.description}</p>}
              </div>
              {hasImages && (
                <div className="pf-pa-gallery">
                  {item.images.map((fn, i) => (
                    <a
                      key={i}
                      href={`/uploads/achievements/${fn}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pf-pa-thumb"
                    >
                      <img src={`/uploads/achievements/${fn}`} alt="" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ── main ── */
const Profile = () => {
  const lang = useLang();
  const { username } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [effectEls, setEffectEls] = useState([]);
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState('about'); // 'about' | 'blog' | 'achievements'
  const [blogCount, setBlogCount] = useState(0);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setUser(null);
    setBlogCount(0);

    // Fetch post count in parallel to determine whether to show Blog tab
    fetch(`/api/profile/${username}/posts`)
      .then(r => r.ok ? r.json() : { posts: [] })
      .then(d => setBlogCount(Array.isArray(d.posts) ? d.posts.length : 0))
      .catch(() => setBlogCount(0));

    fetch(`/api/profile/${username}`)
      .then((r) => {
        if (!r.ok) throw new Error('nf');
        return r.json();
      })
      .then((d) => {
        if (!d.user) throw new Error('nf');
        setUser(d.user);
        const eff = d.user.profileEffect || 'none';
        const gen = EFFECT_GENERATORS[eff] || EFFECT_GENERATORS.none;
        setEffectEls(gen());
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [username]);

  const copyUsername = () => {
    if (!user) return;
    const text = `@${user.username}`;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500); };
    // Prefer the async Clipboard API, but iOS Safari only exposes it over
    // HTTPS / secure contexts. Fall back to a hidden textarea + execCommand.
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(done).catch(() => {
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          done();
        } catch (_) {}
      });
    } else {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        done();
      } catch (_) {}
    }
  };

  if (loading) {
    return (
      <section className="profile-page theme-space">
        <div className="pf-state">
          <div className="pf-spinner" />
          <p>Loading profile…</p>
        </div>
      </section>
    );
  }

  if (notFound || !user) {
    return (
      <section className="profile-page theme-hacker">
        <div className="pf-state">
          <div className="pf-404">404</div>
          <h2>{lang.PROFILE_NOT_FOUND || 'ISPer not found'}</h2>
          <p>
            The user <strong>@{username}</strong> doesn't exist or has no public profile.
          </p>
          <Link to="/members" className="pf-btn pf-btn-ghost">
            ← Back to Members
          </Link>
        </div>
      </section>
    );
  }

  const theme = user.profileTheme || 'classic';
  const effect = user.profileEffect || 'none';
  const cursor = user.profileCursor || 'default';
  // Custom uploaded cursor feature was removed; legacy 'custom' value falls back to default.
  const customCursorStyle = undefined;
  const roles = Array.isArray(user.role) ? user.role : user.role ? [user.role] : [];
  const titleLabel = user.title ? (TITLE_MAP[user.title] || user.title) : null;
  // If user has a title, show it as the primary "role" instead of generic "Ban Điều Hành"
  const displayRole = titleLabel || getDisplayRole(roles);
  const primaryBan = pickPrimaryBan(roles);
  const allBans = pickAllBans(roles);
  const banLabel = allBans.length ? allBans.map(b => ROLE_MAP[b]).join(' · ') : null;

  const roleHistory = (() => {
    const entries = [];
    const currentLabel = titleLabel || banLabel;
    if (user.gen && currentLabel) {
      entries.push({ gen: String(user.gen), label: currentLabel });
    }
    if (Array.isArray(user.genHistory)) {
      user.genHistory.forEach(h => {
        if (!h || !h.gen) return;
        const g = String(h.gen);
        if (entries.some(e => e.gen === g)) return;
        const hTitle = h.title ? (TITLE_MAP[h.title] || h.title) : null;
        const hBans = pickAllBans(h.role);
        const hBansLabel = hBans.length ? hBans.map(b => ROLE_MAP[b]).join(' · ') : null;
        const label = hTitle || hBansLabel;
        if (label) entries.push({ gen: g, label });
      });
    }
    entries.sort((a, b) => Number(a.gen) - Number(b.gen));
    return entries;
  })();
  const rawPositionLabel = POSITION_MAP[user.position] || user.position || null;
  const positionLabel = banLabel || rawPositionLabel;
  const ctfLabel = user.ctfField ? CTF_FIELD_MAP[user.ctfField] || user.ctfField : null;
  const ctfIcon = user.ctfField ? CTF_ICON[user.ctfField] || '⚐' : null;
  const heroPhotos = (() => {
    const list = Array.isArray(user.photos) ? [...user.photos] : [];
    if (user.avatar) {
      // Remove avatar from photos if it appears, then prepend
      const filtered = list.filter(p => p !== user.avatar);
      return [user.avatar, ...filtered];
    }
    return list;
  })();
  const hasPhotos = heroPhotos.length > 0;

  return (
    <section className={`profile-page theme-${theme} cursor-${cursor}`} style={customCursorStyle}>
      <div className="profile-terminal-cue" aria-hidden="true">
        <span>&gt;_ {user.username || 'guest'}@ispclub:~$ sudo ./ispclub --showprofile</span>
        <span className="profile-terminal-caret" />
      </div>
      <MusicPlayer
        playlist={Array.isArray(user.musicPlaylist) ? user.musicPlaylist : (user.profileMusic ? [{ id: 1, ...user.profileMusic }] : [])}
        settings={user.musicSettings || { autoPlay: false, loopMode: 'none' }}
        ownerName={user.fullName || user.username}
      />
      <div className={`profile-effects effect-${effect}`}>
        {effectEls.map((el) => (
          <div key={el.key} className={el.cls} style={el.style}>
            {el.text}
          </div>
        ))}
      </div>

      <div className="pf-shell pf-shell-layout">
        {/* LEFT SIDEBAR NAV */}
        <aside className="pf-sidenav">
          <div className="pf-sidenav-sticky">
            <div className="pf-sidenav-header">
              <div className="pf-sidenav-avatar">
                {user.avatar ? (
                  <img src={`/uploads/profiles/${user.avatar}`} alt={user.fullName} />
                ) : (
                  <span>{(user.fullName || user.username || 'A').charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="pf-sidenav-name">{user.fullName || user.username}</div>
              <div className="pf-sidenav-handle">@{user.username}</div>
            </div>
            <nav className="pf-sidenav-list">
              <button
                type="button"
                className={`pf-sidenav-item ${view === 'about' ? 'active' : ''}`}
                onClick={() => setView('about')}
              >
                <span className="pf-sidenav-ic" aria-hidden="true">👤</span>
                About
              </button>
              {blogCount > 0 && (
                <button
                  type="button"
                  className={`pf-sidenav-item ${view === 'blog' ? 'active' : ''}`}
                  onClick={() => setView('blog')}
                >
                  <span className="pf-sidenav-ic" aria-hidden="true">📝</span>
                  Blog
                </button>
              )}
              {(user.personalAchievements && user.personalAchievements.length > 0) && (
                <button
                  type="button"
                  className={`pf-sidenav-item ${view === 'achievements' ? 'active' : ''}`}
                  onClick={() => setView('achievements')}
                >
                  <span className="pf-sidenav-ic" aria-hidden="true">🏆</span>
                  Achievements
                </button>
              )}
            </nav>
            <div className="pf-sidenav-footer">
              <Link to="/members" className="pf-back-link">← Members</Link>
              <span className="pf-brand">{lang.PROFILE_BRAND || 'ISP CLUB'}</span>
            </div>
          </div>
        </aside>

        <main className="pf-main-content">
        {view === 'about' && (
          <>
        {/* HERO */}
        <header className="pf-hero">
          <div className="pf-hero-photo">
            {hasPhotos ? (
              <PhotoCarousel photos={heroPhotos} />
            ) : (
              <InitialsAvatar name={user.fullName} />
            )}
            <div className="pf-photo-deco" aria-hidden="true" />
          </div>

          <div className="pf-hero-info">
            <div className="pf-hero-eyebrow">
              <span className="pf-eyebrow-dot" />
              <span>{lang.PROFILE_BRAND_SUB || 'ISP CLUB · ISPer Profile'}</span>
            </div>

            <FitName name={user.fullName} />

            <button
              type="button"
              className="pf-hero-username"
              onClick={copyUsername}
              title={lang.PROFILE_CLICK_COPY || 'Click to copy'}
            >
              <span>@{user.username}</span>
              <span className="pf-copy-hint">{copied ? (lang.COMMON_COPIED || 'Copied!') : (lang.COMMON_COPY || 'Copy')}</span>
            </button>

            <div className="pf-hero-tags">
              {roleHistory.length > 0 ? (
                roleHistory.map((e, i) => (
                  <span key={`tag-role-${e.gen}-${i}`} className={`pf-tag pf-tag-role${e.title ? ` pf-tag-t-${e.title}` : ''}`}>
                    Gen {e.gen} — {e.label}
                  </span>
                ))
              ) : (
                displayRole && <span className={`pf-tag pf-tag-role${user.title ? ` pf-tag-t-${user.title}` : ''}`}>{displayRole}</span>
              )}
              {positionLabel && roleHistory.length === 0 && <span className="pf-tag pf-tag-pos">{positionLabel}</span>}
              {roleHistory.length > 0 && rawPositionLabel && <span className={`pf-tag pf-tag-pos${user.position === 'founder' ? ' pf-tag-founder' : ''}`}>{user.position === 'founder' ? '★ ' : ''}{rawPositionLabel}</span>}
              {user.gen && roleHistory.length === 0 && <span className="pf-tag pf-tag-gen">Gen {user.gen}</span>}
            </div>

            {user.bio && <p className="pf-hero-bio">{user.bio}</p>}

            {ctfLabel && (
              <div className="pf-ctf-badge">
                <span className="pf-ctf-icon">{ctfIcon}</span>
                <div className="pf-ctf-meta">
                  <span className="pf-ctf-label">{lang.PROFILE_CTF_FIELD || 'CTF Field'}</span>
                  <span className="pf-ctf-value">{ctfLabel}</span>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* STATS */}
        <section className="pf-stats">
          {user.gen && (
            <div className="pf-stat">
              <span className="pf-stat-label">{lang.PROFILE_GEN || 'Generation'}</span>
              <span className="pf-stat-value">Gen {user.gen}</span>
            </div>
          )}
          {ctfLabel && (
            <div className="pf-stat">
              <span className="pf-stat-label">{lang.PROFILE_CTF_SPECIALTY || 'CTF Specialty'}</span>
              <span className="pf-stat-value">{ctfLabel}</span>
            </div>
          )}
          {rawPositionLabel && (
            <div className={`pf-stat${user.position === 'founder' ? ' pf-stat-founder' : ''}`}>
              <span className="pf-stat-label">{lang.PROFILE_POSITION || 'Position'}</span>
              <span className="pf-stat-value">
                {user.position === 'founder' && <span className="pf-founder-star" aria-hidden="true">★</span>}
                {rawPositionLabel}
              </span>
            </div>
          )}
          {(roleHistory.length > 0 || displayRole) && (
            <div className="pf-stat">
              <span className="pf-stat-label">{lang.PROFILE_ROLE || 'Role'}</span>
              {roleHistory.length > 0 ? (
                <span className="pf-stat-value pf-role-history">
                  {roleHistory.map((e, i) => (
                    <React.Fragment key={`${e.gen}-${i}`}>
                      {i > 0 && <br />}
                      <span>Gen {e.gen} — {e.label}</span>
                    </React.Fragment>
                  ))}
                </span>
              ) : (
                <span className="pf-stat-value">{displayRole}</span>
              )}
            </div>
          )}
        </section>

        {/* GALLERY */}
        {user.photos && user.photos.length > 1 && (
          <section className="pf-section">
            <div className="pf-section-head">
              <h2 className="pf-section-title">{lang.PROFILE_GALLERY || 'Gallery'}</h2>
              <span className="pf-section-count">{user.photos.length} photos</span>
            </div>
            <div className="pf-gallery">
              {user.photos.map((p, i) => (
                <a
                  key={i}
                  href={`/uploads/profiles/${p}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pf-gallery-item"
                >
                  <img src={`/uploads/profiles/${p}`} alt="" />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* CONTACT */}
        <section className="pf-section">
          <div className="pf-section-head">
            <h2 className="pf-section-title">{lang.PROFILE_GET_IN_TOUCH || 'Get in touch'}</h2>
          </div>
          <div className="pf-contact-grid">
            {user.email && (
              <a href={`mailto:${user.email}`} className="pf-contact-card">
                <span className="pf-contact-ic">✉</span>
                <div>
                  <span className="pf-contact-k">{lang.COMMON_EMAIL || 'Email'}</span>
                  <span className="pf-contact-v">{user.email}</span>
                </div>
              </a>
            )}
            {user.phone && (
              <a href={`tel:${user.phone}`} className="pf-contact-card">
                <span className="pf-contact-ic">☎</span>
                <div>
                  <span className="pf-contact-k">{lang.COMMON_PHONE || 'Phone'}</span>
                  <span className="pf-contact-v">{user.phone}</span>
                </div>
              </a>
            )}
            {(() => {
              const list = Array.isArray(user.websites) && user.websites.length > 0
                ? user.websites
                : (user.website ? [{ label: 'Website', url: user.website }] : []);
              return list.map((w, i) => (
                <a
                  key={i}
                  href={w.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pf-contact-card"
                >
                  <span className="pf-contact-ic">🌐</span>
                  <div>
                    <span className="pf-contact-k">{w.label || 'Website'}</span>
                    <span className="pf-contact-v">{w.url}</span>
                  </div>
                </a>
              ));
            })()}
          </div>
        </section>
          </>
        )}

        {view === 'blog' && blogCount > 0 && (
          <div className="pf-view-section">
            <div className="pf-view-head">
              <h2 className="pf-view-title">{lang.PROFILE_BLOG || 'Blog'}</h2>
              <p className="pf-view-sub">Bài viết cá nhân của {user.fullName || user.username}</p>
            </div>
            <PersonalBlog username={user.username} />
          </div>
        )}

        {view === 'achievements' && (user.personalAchievements && user.personalAchievements.length > 0) && (
          <div className="pf-view-section">
            <div className="pf-view-head">
              <h2 className="pf-view-title">{lang.PROFILE_ACHIEVEMENTS || 'Achievements'}</h2>
              <p className="pf-view-sub">Thành tựu cá nhân của {user.fullName || user.username}</p>
            </div>
            <PersonalAchievements items={user.personalAchievements || []} />
          </div>
        )}
        </main>
      </div>
    </section>
  );
};

export default Profile;
